/** @file Applies compiled draw-order fix ops (swap, moveAbove, moveBelow) to a draw order in place. */
import type { CompiledDrawOrderOp } from '../skeletonData';

/** Position of slot in order, or -1. */
function indexIn(order: Int32Array, slot: number): number {
  for (let i = 0; i < order.length; i++) {
    if (order[i] === slot) return i;
  }
  return -1;
}

/** Moves the slot at index `from` to index `to`, shifting the slots in between (no allocation). */
function moveTo(order: Int32Array, from: number, to: number): void {
  const slot = order[from] ?? 0;
  if (from < to) order.copyWithin(from, from + 1, to + 1);
  else order.copyWithin(to + 1, to, from);
  order[to] = slot;
}

/**
 * Applies ops in order; index 0 draws first (bottom). swap exchanges two slots; moveAbove puts op[1] right after
 * (above) op[2]; moveBelow right before (below) it. Ops naming a slot outside `order`, or one slot twice, are skipped.
 */
export function applyDrawOrderOps(order: Int32Array, ops: readonly CompiledDrawOrderOp[]): void {
  for (let k = 0; k < ops.length; k++) {
    const op = ops[k];
    if (op === undefined) continue;
    const a = indexIn(order, op[1]);
    const b = indexIn(order, op[2]);
    if (a < 0 || b < 0 || a === b) continue;
    if (op[0] === 'swap') {
      order[a] = op[2];
      order[b] = op[1];
    } else if (op[0] === 'moveAbove') {
      moveTo(order, a, a < b ? b : b + 1);
    } else {
      moveTo(order, a, a < b ? b - 1 : b);
    }
  }
}
